import { subDays, differenceInCalendarDays, format } from 'date-fns';
import { Viewer } from 'constants/user';
import { ENABLE_OPTION_FLOW_READ_MODE } from 'utils/shared/trades/optionFlowRead';
import {
  Trade,
  ColumnType,
  SortDirection,
  MobileLayout,
  EquityPrint,
} from 'utils/shared/trades/types';
import { GetTickerOptionFlowQuery, GetEquityPrintFlowFullQuery } from 'types/generated/client';

/** Page data injected via window.injectedPageData (SSR / puppeteer). Can contain both option-flow and equity-print-flow rows. */
export type InjectedPageData = GetTickerOptionFlowQuery & Partial<GetEquityPrintFlowFullQuery>;
import { State, type FlowMetricMode } from 'utils/shared/trades/presets';
import { DateFilterValue } from 'components/DateFilter';
import type { FlowAccess } from 'components/MarketDataStatus';

// Max day-over-day bars in the trends graph. Sized to fit a full month of
// trading days so longer timeframes actually render more context (the trend's
// date window is independently capped at a month in getTrendDateRange).
export const MAX_BAR_GRAPH_DAYS = 23;

export enum ActivePage {
  Options = 'options',
  Screener = 'screener',
}

export type Trades = Trade[];

export interface OwnProps {}

export enum FeedType {
  Smart = 'SMART',
  Full = 'FULL',
  Dark = 'DARK',
  Lit = 'LIT',
  All = 'ALL',
}

export enum ViewType {
  Flow = 'FLOW',
  Chain = 'CHAIN',
  Graph = 'GRAPH',
}

export enum ValueType {
  Net = 'NET',
  Total = 'TOTAL',
}

export interface Props extends OwnProps {
  viewer: Viewer;
  injectedPageData?: InjectedPageData;
  activeTicker: string;
  setActiveTicker: (ticker: string) => void;
}

export interface TradeProps {
  trades?: Trades;
  activeTicker: string;
  flowSortKey: string;
  setFlowSortKey: (key: string) => void;
  flowSortDirection: SortDirection;
  setFlowSortDirection: (direction: SortDirection) => void;
  feedType: FeedType;
  setFeedType: (feedType: FeedType) => void;
  mobileLayout?: MobileLayout;
  setMobileLayout?: (mobileLayout: MobileLayout) => void;
  injectedPageData?: any;
  isFilterActive?: boolean;
  isLoading?: boolean;
  flowSideLabel?: string;
  metricMode?: FlowMetricMode;
}

export interface OverviewProps extends Omit<Props, 'setActiveTicker'> {
  sweepTrades?: Trades;
  unusualTrades?: Trades;
  dailyVolume: number;
  trades?: Trades;
  activeTicker: string;
  putFlow: number;
  metricMode?: FlowMetricMode;
}

export interface BarChartSectionProps {
  activeTicker: string;
  trades: Trades;
  metricMode?: FlowMetricMode;
  showMetricModeControls?: boolean;
}

export interface TradesByDate {
  date: string;
  timestamp: number;
  // Volume
  callVolume: number;
  unusualCallVolume: number;
  putVolume: number;
  unusualPutVolume: number;
  // Premium
  callPremium: number;
  unusualCallPremium: number;
  putPremium: number;
  unusualPutPremium: number;
  bullishVolume?: number;
  bearishVolume?: number;
  neutralVolume?: number;
  bullishPremium?: number;
  bearishPremium?: number;
  neutralPremium?: number;
  // Directional bid/ask net values
  netVolume: number;
  netPremium: number;
}

export interface TradesByExpiry {
  expiry: string;
  timestamp: number;
  // Volume
  callVolume: number;
  unusualCallVolume: number;
  putVolume: number;
  unusualPutVolume: number;
  // Premium
  callPremium: number;
  unusualCallPremium: number;
  putPremium: number;
  unusualPutPremium: number;
  bullishVolume?: number;
  bearishVolume?: number;
  neutralVolume?: number;
  bullishPremium?: number;
  bearishPremium?: number;
  neutralPremium?: number;
  // Directional bid/ask net values
  netVolume: number;
  netPremium: number;
}

export interface BarChartProps {
  tradesByDate: TradesByDate[];
  label: string;
  prefix: string;
  formatter: (num: number) => string | number;
  dataType?: 'volume' | 'premium';
  color?: string;
  sortKey?: string;
  valueType?: ValueType;
}

export interface BarChartExpiryProps {
  tradesByExpiry: TradesByExpiry[];
  label: string;
  prefix: string;
  formatter: (num: number) => string | number;
  dataType: 'volume' | 'premium';
  valueType?: ValueType;
}

export interface TradesByStrike {
  strike: number;
  // Volume
  callVolume: number;
  unusualCallVolume: number;
  putVolume: number;
  unusualPutVolume: number;
  // Premium
  callPremium: number;
  unusualCallPremium: number;
  putPremium: number;
  unusualPutPremium: number;
  bullishVolume?: number;
  bearishVolume?: number;
  neutralVolume?: number;
  bullishPremium?: number;
  bearishPremium?: number;
  neutralPremium?: number;
  // Directional bid/ask net values
  netVolume: number;
  netPremium: number;
}

export interface BarChartStrikeProps {
  tradesByStrike: TradesByStrike[];
  label: string;
  prefix: string;
  formatter: (num: number) => string | number;
  dataType: 'volume' | 'premium';
  valueType?: ValueType;
}

export const DEFAULT_FLOW_KEY_INDEX = 0;
export const FLOW_TABLE_HEADERS = [
  { name: 'Date', sortKey: 'date', columnType: ColumnType.Date },
  { name: 'Time', sortKey: 'createdAt', columnType: ColumnType.Date },
  { name: 'C/P', sortKey: 'cp', columnType: ColumnType.Text },
  { name: 'Side', sortKey: 'tradeDirection', columnType: ColumnType.Text },
  { name: 'Read', sortKey: 'sideRead', columnType: ColumnType.Text },
  { name: 'Expiry', sortKey: 'expiry', columnType: ColumnType.Date },
  { name: 'Spot', sortKey: 'spot', columnType: ColumnType.Number },
  { name: 'Strike', sortKey: 'strike', columnType: ColumnType.Number },
  { name: 'OTM', sortKey: 'otmPercent', columnType: ColumnType.Number },
  { name: 'Price', sortKey: 'price', columnType: ColumnType.Number },
  { name: 'Size', sortKey: 'size', columnType: ColumnType.Number },
  { name: 'Open Interest', sortKey: 'openInterest', columnType: ColumnType.Number },
  { name: 'Implied Vol', sortKey: 'impliedVol', columnType: ColumnType.Number },
  { name: 'Type', sortKey: 'type', columnType: ColumnType.Text },
  { name: 'Premium', sortKey: 'premium', columnType: ColumnType.Number },
  { name: 'Heat Score', sortKey: 'heatScore', columnType: ColumnType.Number },
].filter((header) => ENABLE_OPTION_FLOW_READ_MODE || header.name !== 'Read');
export const DEFAULT_FLOW_KEY = FLOW_TABLE_HEADERS[DEFAULT_FLOW_KEY_INDEX].sortKey;

export interface TickerDarkPoolTableProps {
  activeTicker: string;
  viewer: Viewer;
  activeTimeFilter: number;
  injectedPageData?: InjectedPageData;
  hideHeader?: boolean;
  hideLevelsColumn?: boolean;
  tickerDetailsMap?: Record<string, { name: string; sector: string }>;
  filterState?: State;
  /** Applied date bounds (yyyy-MM-dd). Null/undefined = fire the unbounded Latest query. */
  dateRange?: DateRangeVars | null;
  /** When true, narrow display to the most recent trading day (Latest preset). */
  isLatestPreset?: boolean;
  /** When true, use the rolling latest query and apply date bounds client-side. */
  useLatestData?: boolean;
  /** Applied date filter. Presets are resolved against this table's loaded rows. */
  dateFilterValue?: DateFilterValue;
}

export const WEEK_DAYS = 6;
export const TWO_WEEK_DAYS = 13;
/** 27 days back + anchor = 28-day inclusive range (Week × 4) */
export const MONTH_DAYS = 27;

/** Variables sent to the historical GraphQL queries (yyyy-MM-dd strings). */
export interface DateRangeVars {
  startDate: string;
  endDate: string;
}

/** Check if a trade date string falls within N calendar days of maxTradeDate */
export function isWithinDayRange(tradeDateStr: string, maxTradeDate: Date, days: number): boolean {
  const tradeDate = new Date(tradeDateStr);
  return differenceInCalendarDays(maxTradeDate, tradeDate) <= days;
}

export enum TimeFilters {
  Today,
  Week,
  TwoWeeks,
  Month,
}

export const TIME_FILTERS = [
  { name: 'Most Recent', key: TimeFilters.Today },
  { name: 'Week', key: TimeFilters.Week },
  { name: '2 Weeks', key: TimeFilters.TwoWeeks },
  { name: 'Month', key: TimeFilters.Month },
];

/** Preset keys that map to the existing TimeFilters enum */
export const DATE_FILTER_PRESET_KEYS = {
  latest: 'latest',
  week: 'week',
  twoWeeks: 'twoWeeks',
  month: 'month',
} as const;

/** The applied filter on first load and after Clear: the newest session. */
export const DEFAULT_DATE_FILTER_VALUE: DateFilterValue = {
  preset: DATE_FILTER_PRESET_KEYS.latest,
  startDate: null,
  endDate: null,
};

export const DATE_FILTER_PRESETS = [
  { label: 'Latest', triggerLabel: 'Latest', key: DATE_FILTER_PRESET_KEYS.latest },
  { label: 'Week', key: DATE_FILTER_PRESET_KEYS.week },
  { label: '2 Weeks', key: DATE_FILTER_PRESET_KEYS.twoWeeks },
  { label: 'Month', key: DATE_FILTER_PRESET_KEYS.month },
];

/** Map a DateFilter preset key back to the legacy TimeFilters index for child components */
export function presetKeyToTimeFilterIndex(presetKey: string | null): number {
  const keyForPreset = (() => {
    switch (presetKey) {
      case DATE_FILTER_PRESET_KEYS.week:
        return TimeFilters.Week;
      case DATE_FILTER_PRESET_KEYS.twoWeeks:
        return TimeFilters.TwoWeeks;
      case DATE_FILTER_PRESET_KEYS.month:
        return TimeFilters.Month;
      case DATE_FILTER_PRESET_KEYS.latest:
      default:
        return TimeFilters.Today;
    }
  })();
  const idx = TIME_FILTERS.findIndex((f) => f.key === keyForPreset);
  return idx === -1 ? 0 : idx;
}

/** Compute start/end dates for a preset given the most recent trade date */
export function computeDatesForPreset(
  presetKey: string,
  maxTradeDate: Date,
): { startDate: Date; endDate: Date } {
  const endDate = new Date(maxTradeDate);
  let startDate: Date;

  switch (presetKey) {
    case DATE_FILTER_PRESET_KEYS.week:
      startDate = subDays(endDate, WEEK_DAYS);
      break;
    case DATE_FILTER_PRESET_KEYS.twoWeeks:
      startDate = subDays(endDate, TWO_WEEK_DAYS);
      break;
    case DATE_FILTER_PRESET_KEYS.month:
      startDate = subDays(endDate, MONTH_DAYS);
      break;
    case DATE_FILTER_PRESET_KEYS.latest:
    default:
      startDate = new Date(endDate);
      break;
  }

  return { startDate, endDate };
}

function formatDateRangeVars(range: { startDate: Date; endDate: Date }): DateRangeVars {
  return {
    startDate: format(range.startDate, 'yyyy-MM-dd'),
    endDate: format(range.endDate, 'yyyy-MM-dd'),
  };
}

function parseLocalDateString(dateString: string): Date | null {
  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) return null;

  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getMaxTradeDate<T extends { date?: string | null }>(trades: T[]): string | null {
  return trades.reduce<string | null>((maxDate, trade) => {
    if (!trade.date) return maxDate;
    return !maxDate || trade.date > maxDate ? trade.date : maxDate;
  }, null);
}

/**
 * Build historical-query date variables from a custom DateFilterValue.
 * Presets stay on the rolling Latest query and are narrowed client-side
 * against each data source's latest loaded trade date.
 */
export function dateFilterToQueryVars(value: DateFilterValue): DateRangeVars | null {
  if (value.preset !== null) return null;
  if (!value.startDate || !value.endDate) return null;
  return formatDateRangeVars({ startDate: value.startDate, endDate: value.endDate });
}

export function shouldUseLatestDataForDateFilter(
  value: DateFilterValue,
  dateVars: DateRangeVars | null,
  today: Date = new Date(),
): boolean {
  if (value.preset !== null) return true;
  if (!dateVars) return true;

  const todayStart = new Date(today);
  todayStart.setHours(0, 0, 0, 0);
  const minStart = format(subDays(todayStart, MONTH_DAYS), 'yyyy-MM-dd');
  return dateVars.startDate >= minStart;
}

/**
 * Every date filter except Latest belongs to subscribers. A free or anonymous viewer receives the
 * latest session and nothing before it: Hasura caps the free views at 20 rows per request for
 * anonymous viewers and 100 for signed-in free accounts, and the realtime feed adds only the latest
 * session. Latest is the one selection that data fills. A week, a month or a custom range would show
 * that same session under a wider label, or an empty table when the range leaves it out.
 */
export function isSubscriberDateFilter(value: DateFilterValue): boolean {
  return value.preset !== DATE_FILTER_PRESET_KEYS.latest;
}

/** The presets the picker marks with a lock for a viewer on the free feed. */
export const SUBSCRIBER_DATE_FILTER_PRESET_KEYS = DATE_FILTER_PRESETS.map(({ key }) => key).filter(
  (key) => isSubscriberDateFilter({ preset: key, startDate: null, endDate: null }),
);

/**
 * Whether the viewer is known to be on the free feed, so subscriber date filters carry a lock and
 * meet the subscribe popup. Never while the sign-in is still resolving (nothing is shown about a
 * plan until it is known), and never for injected page data (server-side captures).
 */
export function isDateFilterLocked({
  flowAccess,
  hasInjectedPageData,
}: {
  flowAccess: FlowAccess;
  hasInjectedPageData: boolean;
}): boolean {
  return !hasInjectedPageData && flowAccess === 'delayed';
}

/** What applying a date filter should do: take effect, meet the paywall, or wait for sign-in. */
export type DateFilterApplyDecision = 'apply' | 'gate' | 'wait';

/**
 * Latest applies for everyone. Every other preset and every custom range applies for subscribers
 * and injected page data, and meets the paywall for everyone else. While the sign-in is still
 * resolving the answer is `wait`, never a guess: a subscriber must not see the paywall, and a free
 * viewer must not see a range their plan does not fill.
 */
export function getDateFilterApplyDecision({
  value,
  flowAccess,
  hasInjectedPageData,
}: {
  value: DateFilterValue;
  flowAccess: FlowAccess;
  hasInjectedPageData: boolean;
}): DateFilterApplyDecision {
  if (hasInjectedPageData || flowAccess === 'live') return 'apply';
  if (!isSubscriberDateFilter(value)) return 'apply';
  return flowAccess === 'unknown' ? 'wait' : 'gate';
}

export function getDateRangeForDateFilter<T extends { date?: string | null }>(
  value: DateFilterValue,
  trades: T[],
  customDateRange?: DateRangeVars | null,
): DateRangeVars | null {
  if (value.preset === DATE_FILTER_PRESET_KEYS.latest) return null;

  if (value.preset === null) {
    return customDateRange ?? dateFilterToQueryVars(value);
  }

  const maxTradeDate = getMaxTradeDate(trades);
  if (!maxTradeDate) return null;

  const anchorDate = parseLocalDateString(maxTradeDate);
  if (!anchorDate) return null;

  return formatDateRangeVars(computeDatesForPreset(value.preset, anchorDate));
}

export function filterTradesForDateSelection<T extends { date?: string | null }>(
  trades: T[],
  {
    isLatestPreset,
    useLatestData,
    dateRange,
  }: {
    isLatestPreset: boolean;
    useLatestData: boolean;
    dateRange?: DateRangeVars | null;
  },
): T[] {
  if (trades.length === 0) return trades;

  if (isLatestPreset) {
    const maxDate = getMaxTradeDate(trades);
    if (!maxDate) return trades;
    return trades.filter((trade) => trade.date === maxDate);
  }

  if (useLatestData && dateRange) {
    return trades.filter(
      (trade) =>
        !!trade.date && trade.date >= dateRange.startDate && trade.date <= dateRange.endDate,
    );
  }

  return trades;
}

/**
 * Date window for the trend graphs. A trend needs multiple days of context, so
 * it always shows at least a week (this makes "Latest" match the "Week" preset
 * instead of collapsing to a single day) and at most a month, anchored to the
 * most recent loaded trade date (or the end of a selected historical range).
 */
export function getTrendDateRange<T extends { date?: string | null }>(
  trades: T[],
  displayDateRange: DateRangeVars | null,
): DateRangeVars | null {
  const maxTradeDate = getMaxTradeDate(trades);
  const anchor = maxTradeDate ? parseLocalDateString(maxTradeDate) : null;
  if (!anchor) return null;

  const endDate = displayDateRange
    ? parseLocalDateString(displayDateRange.endDate) ?? anchor
    : anchor;
  const selectedStart = displayDateRange
    ? parseLocalDateString(displayDateRange.startDate) ?? endDate
    : endDate;

  const weekStart = subDays(endDate, WEEK_DAYS); // floor: at least a week of context
  const monthStart = subDays(endDate, MONTH_DAYS); // cap: at most a month

  let startDate = selectedStart;
  if (startDate > weekStart) startDate = weekStart;
  if (startDate < monthStart) startDate = monthStart;

  return formatDateRangeVars({ startDate, endDate });
}

export interface DarkPoolAggregatedDetails {
  ticker: string;
  premium: number;
  volume: number;
  lateBuyCount: number;
  lateSellCount: number;
  goldenTradeCount: number;
  trades: EquityPrint[];
}
